import { useState, useCallback } from 'react';
import { showValidationSummary } from '../utils/sweetAlertUtils';

export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: any, formData?: any) => boolean | { [key: string]: string };
  message: string;
}

// Support both string keys and nested objects
export interface ValidationRules {
  [key: string]: ValidationRule[] | ValidationRules;
}

// Helper function to get nested property value
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
};

// Helper function to set nested property value
const setNestedValue = (obj: any, path: string, value: string): void => {
  const keys = path.split('.');
  const lastKey = keys.pop()!;
  const target = keys.reduce((current, key) => {
    if (!current[key]) current[key] = {};
    return current[key];
  }, obj);
  target[lastKey] = value;
};

// Helper function to flatten nested validation rules
const flattenValidationRules = (rules: ValidationRules, prefix = ''): Record<string, ValidationRule[]> => {
  const flattened: Record<string, ValidationRule[]> = {};
  
  Object.entries(rules).forEach(([key, value]) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    
    if (Array.isArray(value)) {
      // It's a ValidationRule array
      flattened[fullKey] = value;
    } else {
      // It's a nested ValidationRules object
      const nested = flattenValidationRules(value as ValidationRules, fullKey);
      Object.assign(flattened, nested);
    }
  });
  
  return flattened;
};

export const useFormValidation = <T extends object>(rules: ValidationRules) => {
  const [errors, setErrors] = useState<any>({});
  const [isValid, setIsValid] = useState(false);

  const validate = useCallback((data: T, showSummary: boolean = true): boolean => {
    const flatErrors: { [key: string]: string } = {};
    
    // Flatten the nested rules structure
    const flattenedRules = flattenValidationRules(rules);

    Object.entries(flattenedRules).forEach(([field, fieldRules]) => {
      // Handle nested properties like 'memberFirearm.firearmTypeId'
      const value = field.includes('.') 
        ? getNestedValue(data, field)
        : data[field as keyof T];

      for (const rule of fieldRules) {
        // Check for required fields, allowing 0 as a valid value
        if (rule.required && (value === undefined || value === null || value === '')) {
          flatErrors[field] = rule.message;
          break;
        }
        if (rule.minLength && value && value.length < rule.minLength) {
          flatErrors[field] = rule.message;
          break;
        }
        if (rule.maxLength && value && value.length > rule.maxLength) {
          flatErrors[field] = rule.message;
          break;
        }
        if (rule.pattern && value && !rule.pattern.test(value)) {
          flatErrors[field] = rule.message;
          break;
        }
        if (rule.custom) {
          const customResult = rule.custom(value, data);
          
          // Enhanced custom validation that can return specific field errors
          if (typeof customResult === 'object' && customResult !== null) {
            // Custom validation returned an object with specific field errors
            Object.entries(customResult).forEach(([errorField, errorMessage]) => {
              if (errorMessage) {
                flatErrors[errorField] = errorMessage as string;
              }
            });
          } else if (customResult === false) {
            // Standard boolean return - validation failed
            flatErrors[field] = rule.message;
          }
          // If customResult === true, validation passed, continue to next rule
          break;
        }
      }
    });

    // Convert flat errors back to nested object structure
    const nestedErrors: any = {};
    Object.entries(flatErrors).forEach(([path, message]) => {
      setNestedValue(nestedErrors, path, message);
    });

    setErrors(nestedErrors);
    const valid = Object.keys(flatErrors).length === 0;
    setIsValid(valid);
    
    // Show validation summary if validation failed and showSummary is true
    // Fire this as a side effect without blocking the return
    if (!valid && showSummary) {
      showValidationSummary(flatErrors);
    }
    
    return valid;
  }, [rules]);

  const clearError = useCallback((field: string) => {
    setErrors((prev: any) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  return { errors, validate, isValid, clearError };
};